import {
  StatusBadge,
  type StatusBadgeStatus,
  StatusBadgeStatuses,
} from '@procurify/ui'
import { type MessageDescriptor } from 'react-intl'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import {
  type GlobalSearchResultStatusEnum,
  GlobalSearchResultStatuses,
  type GlobalSearchResultTypeEnum,
  GlobalSearchResultTypes,
} from '../../../../../../value-objects/enums'

const STATUS_BADGE_TYPE_MAP: Partial<
  Record<
    GlobalSearchResultTypeEnum,
    Partial<Record<GlobalSearchResultStatusEnum, StatusBadgeStatus>>
  >
> = {
  [GlobalSearchResultTypes.ORDER_REQUEST]: {
    [GlobalSearchResultStatuses.PENDING_APPROVAL]:
      StatusBadgeStatuses.INCOMPLETE,
    [GlobalSearchResultStatuses.APPROVED]: StatusBadgeStatuses.IN_PROGRESS,
    [GlobalSearchResultStatuses.DENIED]: StatusBadgeStatuses.WARNING,
    [GlobalSearchResultStatuses.PURCHASED]: StatusBadgeStatuses.IN_PROGRESS,
    [GlobalSearchResultStatuses.RECEIVED]: StatusBadgeStatuses.SUCCESS,
  },
  [GlobalSearchResultTypes.PURCHASE_ORDER]: {
    [GlobalSearchResultStatuses.OPEN]: StatusBadgeStatuses.IN_PROGRESS,
    [GlobalSearchResultStatuses.REOPENED]: StatusBadgeStatuses.IN_PROGRESS,
    [GlobalSearchResultStatuses.CANCELLED]: StatusBadgeStatuses.WARNING,
    [GlobalSearchResultStatuses.CLOSED]: StatusBadgeStatuses.SUCCESS,
    [GlobalSearchResultStatuses.PAID]: StatusBadgeStatuses.SUCCESS,
  },
  [GlobalSearchResultTypes.EXPENSE_REQUEST]: {
    [GlobalSearchResultStatuses.PENDING_APPROVAL]:
      StatusBadgeStatuses.INCOMPLETE,
    [GlobalSearchResultStatuses.APPROVED]: StatusBadgeStatuses.SUCCESS,
    [GlobalSearchResultStatuses.DENIED]: StatusBadgeStatuses.WARNING,
  },
  [GlobalSearchResultTypes.TRAVEL_REQUEST]: {
    [GlobalSearchResultStatuses.PENDING_APPROVAL]:
      StatusBadgeStatuses.INCOMPLETE,
    [GlobalSearchResultStatuses.APPROVED]: StatusBadgeStatuses.SUCCESS,
    [GlobalSearchResultStatuses.DENIED]: StatusBadgeStatuses.WARNING,
  },
  [GlobalSearchResultTypes.BILL]: {
    [GlobalSearchResultStatuses.DRAFT]: StatusBadgeStatuses.INCOMPLETE,
    [GlobalSearchResultStatuses.PENDING_APPROVAL]:
      StatusBadgeStatuses.INCOMPLETE,
    [GlobalSearchResultStatuses.APPROVED]: StatusBadgeStatuses.IN_PROGRESS,
    [GlobalSearchResultStatuses.DENIED]: StatusBadgeStatuses.WARNING,
    [GlobalSearchResultStatuses.PAID]: StatusBadgeStatuses.SUCCESS,
  },
  [GlobalSearchResultTypes.PAYMENT]: {
    [GlobalSearchResultStatuses.DRAFT]: StatusBadgeStatuses.INCOMPLETE,
    [GlobalSearchResultStatuses.PENDING_APPROVAL]:
      StatusBadgeStatuses.INCOMPLETE,
    [GlobalSearchResultStatuses.APPROVED]: StatusBadgeStatuses.IN_PROGRESS,
    [GlobalSearchResultStatuses.DENIED]: StatusBadgeStatuses.WARNING,
    [GlobalSearchResultStatuses.SCHEDULED]: StatusBadgeStatuses.IN_PROGRESS,
    [GlobalSearchResultStatuses.PROCESSING]: StatusBadgeStatuses.IN_PROGRESS,
    [GlobalSearchResultStatuses.PAID]: StatusBadgeStatuses.SUCCESS,
    [GlobalSearchResultStatuses.FAILED]: StatusBadgeStatuses.ERROR,
  },
}

const STATUS_BADGE_MESSAGE_MAP: Record<
  GlobalSearchResultStatusEnum,
  MessageDescriptor
> = {
  [GlobalSearchResultStatuses.PENDING_APPROVAL]: messages.pendingApprovalLabel,
  [GlobalSearchResultStatuses.APPROVED]: messages.approvedLabel,
  [GlobalSearchResultStatuses.DENIED]: messages.deniedLabel,
  [GlobalSearchResultStatuses.PURCHASED]: messages.purchasedLabel,
  [GlobalSearchResultStatuses.RECEIVED]: messages.receivedLabel,
  [GlobalSearchResultStatuses.OPEN]: messages.openLabel,
  [GlobalSearchResultStatuses.REOPENED]: messages.reopenedLabel,
  [GlobalSearchResultStatuses.CANCELLED]: messages.cancelledLabel,
  [GlobalSearchResultStatuses.CLOSED]: messages.closedLabel,
  [GlobalSearchResultStatuses.PAID]: messages.paidLabel,
  [GlobalSearchResultStatuses.DRAFT]: messages.draftLabel,
  [GlobalSearchResultStatuses.SCHEDULED]: messages.scheduledLabel,
  [GlobalSearchResultStatuses.PROCESSING]: messages.processingLabel,
  [GlobalSearchResultStatuses.FAILED]: messages.failedLabel,
}

const getStatusBadgeType = (
  resultType: GlobalSearchResultTypeEnum,
  status: GlobalSearchResultStatusEnum
): Nullable<StatusBadgeStatus> => {
  const typeMap = STATUS_BADGE_TYPE_MAP[resultType]

  return typeMap ? (typeMap[status] ?? null) : null
}

const getStatusMessage = (
  status: GlobalSearchResultStatusEnum
): MessageDescriptor => {
  return STATUS_BADGE_MESSAGE_MAP[status]
}

interface IGlobalSearchListItemStatusLabelProps {
  resultType: GlobalSearchResultTypeEnum
  status: GlobalSearchResultStatusEnum
}

export const GlobalSearchListItemStatusLabel = ({
  resultType,
  status,
}: IGlobalSearchListItemStatusLabelProps) => {
  const intl = useProcurifyIntl()
  const statusBadgeType = getStatusBadgeType(resultType, status)

  if (!statusBadgeType) {
    return null
  }

  return (
    <StatusBadge
      type={statusBadgeType}
      text={intl.formatMessage(getStatusMessage(status))}
      size='small'
    />
  )
}
